import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmsCampaignListReport {
    id?: number;
    receiver_number: string;
    status?: number;
    retry_status?: number;
    createdAt: string;
    sms_gateway: {
        name: string
        id: number
    };
    sms_gateways_number: {
        gateway_number: string;
    }
    length: number;
}

interface SmsCampaignState {
    smscampaignlistreportdata: SmsCampaignListReport[];
    info: { count: number; current_page: number; limit: number; total_page: number } | null;
    error: string | null;
    loading: boolean;
}

interface SmsCampaignListParams {
    id: number;
    sms_gateway_name?: string;
    sms_gateway?: number;
    gateway_id?: number;
    gateway_number?: string;
    receiver_number?: string;
    attempt?: number;
    sort_field?: string;
    sort?: 'ASC' | 'DESC';
    status?: number;
    retry_status?: number;
    limit: number;
    page: number;
}

interface SmsCampaignListResponse {
    data: SmsCampaignListReport[];
    info: {
        count: number;
        current_page: number;
        limit: number;
        total_page: number;
    };
}

export const GetallSmsCampaignListReport = createAsyncThunk<
    SmsCampaignListResponse,
    SmsCampaignListParams,
    { rejectValue: string }
>(
    'GetallSmsCampaignListReport',
    async (params, { rejectWithValue }) => {
        try {
            await delay(500)
            const response = await axiosInstance.get(`/api/admin/reports/sms-campaign-list/${params.id}`, {
                params: {
                    'sms_gateway.name': params.sms_gateway_name,
                    gateway_id: params.sms_gateway === 0 ? '' : params.sms_gateway,
                    'sms_gateways_number.gateway_number': params.gateway_number,
                    receiver_number: params.receiver_number,
                    attempt: params.attempt,
                    sort_field: params.sort_field,
                    sort: params.sort,
                    status: params.status,
                    retry_status: params.retry_status,
                    page: params.page,
                    limit: params.limit
                }
            });
            return { data: response.data.data, info: response.data.info };
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);


// ==================AddSmsCampaigns-action-start===========
export const AddSmsCampaignsListRetry = createAsyncThunk<
    SmsCampaignListReport,
    SmsCampaignListReport,
    { rejectValue: string }
>(
    'smscampaignlistreportdata/AddSmsCampaignsListRetry',
    async (retrydata, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`api/admin/sms-campaign/lists/retry`, retrydata);
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// ==================AddSmsCampaigns-action-end===========


const initialState: SmsCampaignState = {
    smscampaignlistreportdata: [],
    info: null,
    error: null,
    loading: false,
};

const smscampaignSlice = createSlice({
    name: "Smscampaignlistreport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ===============get-all-smtp-slice-start============
            .addCase(GetallSmsCampaignListReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(GetallSmsCampaignListReport.fulfilled, (state, action: PayloadAction<{ data: SmsCampaignListReport[], info: { count: number; current_page: number; limit: number; total_page: number } }>) => {
                state.loading = false;
                state.smscampaignlistreportdata = action.payload.data;
                state.info = action.payload.info;
            })
            .addCase(GetallSmsCampaignListReport.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'An error occurred';
            });
        // ===============get-all-smtp-slice-end============

        // =================add-smscampaign-list-retry-slice-start============
        builder
            .addCase(AddSmsCampaignsListRetry.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(AddSmsCampaignsListRetry.fulfilled, (state, action: PayloadAction<SmsCampaignListReport>) => {
                state.loading = false;
                state.smscampaignlistreportdata.push(action.payload);
            })
            .addCase(
                AddSmsCampaignsListRetry.rejected, (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || 'Failed to add smscampaign list retry';
                }
            );
        // =================add-smscampaign-list-retry-slice-end============
    },
});

export default smscampaignSlice.reducer;
